/**
 * SurveyFlow — pure-JS path analyzer for the YulOnTap branching engine.
 *
 * P_BRANCHING_TREE_AUTHORING_1.0 — provides the data backing the Survey
 * Flow Map (tree view + linter warnings) in the editor.
 *
 * Key semantics
 * -------------
 * We distinguish TWO kinds of incoming edges to each question:
 *
 *   1. DIRECT  — main flow start, or an explicit `jump` from a branch
 *                choice, or an explicit `jump` from a question-level
 *                next_step.
 *   2. LINEAR  — falling off the previous question's `next_step={type:
 *                'next'}` (or null). This is the silent path that
 *                produces sibling-branch leaks when a branch follow-up
 *                forgets to terminate.
 *
 * Leak detection compares DIRECT owners only — linear edges represent
 * the very mistake we're trying to catch, so feeding them into
 * exclusivity checks would mask the leak.
 *
 * Exports
 * -------
 *   analyzeSurveyFlow(questions) → {
 *     reachedFrom,            // qid → Set of all incoming tags (any kind)
 *     branchOf,               // qid → { branchingQid, choiceId, choiceLabel }
 *                             //        when the question is exclusively reached
 *                             //        via a single branch tag (jump only)
 *     leaks,                  // [{ qid, leakingIntoQid, fromBranch, intoBranch }]
 *     unreachable,            // qids that no path reaches
 *     tree,                   // ASCII tree of the flow
 *   }
 *   healSiblingBranchLeaks(questions) → {changed, questions, fixedCount}
 */

const isBranching = (q) => q?.response_type === 'multiple_choice_branching';

function outgoing(q, questions) {
    if (!q) return [];
    const qIdx = questions.findIndex((x) => x.id === q.id);
    if (qIdx < 0) return [];
    const linear = () => questions[qIdx + 1]?.id || null;

    const resolveNs = (ns, fallbackTag) => {
        if (!ns || ns.type === 'next')
            return { targetQid: linear(), tag: fallbackTag, kind: 'linear' };
        if (ns.type === 'end')
            return { targetQid: null, tag: fallbackTag, kind: 'end' };
        if (ns.type === 'jump')
            return { targetQid: ns.target_question_id || null, tag: fallbackTag, kind: 'jump' };
        return { targetQid: linear(), tag: fallbackTag, kind: 'linear' };
    };

    if (isBranching(q)) {
        return (q.choices || []).map((c) => {
            const tag = `branch:${q.id}:${c.id}`;
            const edge = resolveNs(c.next_step, tag);
            // Every branch choice edge counts as a DIRECT entry into its
            // target (the choice mints a fresh tag and jumps in).
            return { ...edge, kind: edge.kind === 'linear' ? 'linear-branch' : edge.kind, branchingQid: q.id, choiceId: c.id, choiceLabel: c.label };
        });
    }
    return [resolveNs(q.next_step, null)].map((e) => ({ ...e, branchingQid: null, choiceId: null }));
}

export function analyzeSurveyFlow(questions) {
    const qs = Array.isArray(questions) ? questions : [];
    const byId = new Map(qs.map((q) => [q.id, q]));
    const reachedFrom = new Map();   // qid → Set of all tags
    const directOwners = new Map();  // qid → Set of "owner" tags (jump targets + main entry)

    function visit(qid, incomingTag, isDirect, seen) {
        if (!qid || !byId.has(qid)) return;
        if (seen.has(qid)) return;             // cycle guard
        const set = reachedFrom.get(qid) || new Set();
        if (set.has(`${incomingTag}|${isDirect ? 'D' : 'L'}`)) return;
        set.add(`${incomingTag}|${isDirect ? 'D' : 'L'}`);
        reachedFrom.set(qid, set);
        if (isDirect) {
            const dset = directOwners.get(qid) || new Set();
            dset.add(incomingTag);
            directOwners.set(qid, dset);
        }
        const q = byId.get(qid);
        const nextSeen = new Set(seen);
        nextSeen.add(qid);
        for (const e of outgoing(q, qs)) {
            // For branching questions, every choice's outgoing is a
            // FRESH direct path into its target (or end). For non-
            // branching, an explicit jump is direct; a linear fall-
            // through is NOT direct (it inherits the tag but flags
            // itself as linear so leak detection can spot it).
            let nextIsDirect;
            let fwdTag;
            if (isBranching(q)) {
                nextIsDirect = e.kind === 'jump' || e.kind === 'linear-branch';
                fwdTag = e.tag;
            } else {
                nextIsDirect = e.kind === 'jump';
                fwdTag = incomingTag;
            }
            visit(e.targetQid, fwdTag, nextIsDirect, nextSeen);
        }
    }

    if (qs.length) visit(qs[0].id, 'main', true, new Set());

    // Exclusive branch ownership = a question whose DIRECT owners are
    // a single branch tag (not main, not multiple).
    const branchOf = new Map();
    for (const q of qs) {
        const dset = directOwners.get(q.id);
        if (!dset || dset.size === 0) { branchOf.set(q.id, null); continue; }
        const tags = [...dset];
        const branchTags = tags.filter((t) => t.startsWith('branch:'));
        if (tags.includes('main') || branchTags.length !== 1) {
            branchOf.set(q.id, null);
            continue;
        }
        const [, bqid, cid] = branchTags[0].split(':');
        const bQ = byId.get(bqid);
        const choice = (bQ?.choices || []).find((c) => c.id === cid);
        branchOf.set(q.id, {
            branchingQid: bqid,
            choiceId: cid,
            choiceLabel: choice?.label || {},
        });
    }

    // Leak detection — compare DIRECT owners between Q and Q+1.
    // A leak occurs when Q linear-falls into Q+1, Q is exclusively
    // owned by one branch, Q+1 is exclusively owned by a DIFFERENT
    // branch (both from the same branching question), and Q has no
    // explicit terminator. Crucially we DO NOT need branchOf[Q+1] to
    // be exclusive — directOwners is the source of truth so a Q+1
    // that's also been polluted by Q's linear advance still flags.
    const leaks = [];
    for (const q of qs) {
        if (isBranching(q)) continue;
        const ns = q.next_step;
        const fallsThrough = !ns || ns.type === 'next';
        if (!fallsThrough) continue;
        const qIdx = qs.findIndex((x) => x.id === q.id);
        const nextQ = qs[qIdx + 1];
        if (!nextQ) continue;

        const hereDirect = [...(directOwners.get(q.id) || [])].filter((t) => t.startsWith('branch:'));
        const thereDirect = [...(directOwners.get(nextQ.id) || [])].filter((t) => t.startsWith('branch:'));
        if (hereDirect.length !== 1 || thereDirect.length === 0) continue;

        const [, hereBqid, hereCid] = hereDirect[0].split(':');
        const inSameBranchingQ = thereDirect.some((t) => {
            const [, bqid, cid] = t.split(':');
            return bqid === hereBqid && cid !== hereCid;
        });
        if (!inSameBranchingQ) continue;

        const bQ = byId.get(hereBqid);
        const hereChoice = (bQ?.choices || []).find((c) => c.id === hereCid);
        const otherTag = thereDirect.find((t) => t.split(':')[2] !== hereCid);
        const otherCid = otherTag.split(':')[2];
        const otherChoice = (bQ?.choices || []).find((c) => c.id === otherCid);

        leaks.push({
            qid: q.id,
            leakingIntoQid: nextQ.id,
            fromBranch: {
                branchingQid: hereBqid,
                choiceId: hereCid,
                choiceLabel: hereChoice?.label || {},
            },
            intoBranch: {
                branchingQid: hereBqid,
                choiceId: otherCid,
                choiceLabel: otherChoice?.label || {},
            },
        });
    }

    const unreachable = qs.filter((q) => !reachedFrom.has(q.id)).map((q) => q.id);

    // ASCII tree
    const lines = [];
    const printed = new Set();
    function printNode(qid, prefix, isLast) {
        if (!qid || !byId.has(qid)) return;
        if (printed.has(qid)) { lines.push(`${prefix}${isLast ? '└─ ' : '├─ '}↪ ${shortLabel(byId.get(qid))} (loop guard)`); return; }
        printed.add(qid);
        const q = byId.get(qid);
        lines.push(`${prefix}${isLast ? '└─ ' : '├─ '}${shortLabel(q)}`);
        const childPrefix = prefix + (isLast ? '    ' : '│   ');
        const edges = outgoing(q, qs);
        edges.forEach((e, i) => {
            const last = i === edges.length - 1;
            if (isBranching(q)) {
                const cLabel = e.choiceLabel?.en || e.choiceLabel?.fr || e.choiceLabel?.es || e.choiceId;
                lines.push(`${childPrefix}${last ? '└─ ' : '├─ '}choice: ${cLabel}`);
                const innerPrefix = childPrefix + (last ? '    ' : '│   ');
                if (e.targetQid) printNode(e.targetQid, innerPrefix, true);
                else lines.push(`${innerPrefix}└─ ✅ End survey`);
            } else if (e.targetQid) {
                printNode(e.targetQid, childPrefix, last);
            } else {
                lines.push(`${childPrefix}${last ? '└─ ' : '├─ '}✅ End survey`);
            }
        });
    }
    if (qs.length) printNode(qs[0].id, '', true);

    return { reachedFrom, branchOf, leaks, unreachable, tree: lines.join('\n') };
}

function shortLabel(q) {
    const text = q.text?.en || q.text?.fr || q.text?.es || q.id;
    const tag = isBranching(q) ? ' [branching]' : '';
    return `${text}${tag}`;
}

export function healSiblingBranchLeaks(questions) {
    const { leaks } = analyzeSurveyFlow(questions);
    if (leaks.length === 0) return { changed: false, questions };
    const leakIds = new Set(leaks.map((l) => l.qid));
    const next = questions.map((q) => {
        if (!leakIds.has(q.id)) return q;
        return { ...q, next_step: { type: 'end' } };
    });
    return { changed: true, questions: next, fixedCount: leakIds.size };
}
